/**
 * Error Boundary Component
 * Class component that catches React errors and displays fallback UI
 * 
 * @task FND-FE-003: Error Boundaries
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import { ErrorFallback, ErrorFallbackProps } from './ErrorFallback';
import { errorReporting } from '@/services/errorReporting';

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Callback when user resets the error */
  onReset?: () => void;
  /** Whether to reset error state when props change */
  resetOnPropsChange?: boolean;
  /** Additional context for error reporting */
  context?: Record<string, unknown>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary - Catches JavaScript errors anywhere in child component tree
 * 
 * @example
 * <ErrorBoundary fallback={<CustomError />}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Update state with error info
    this.setState({ errorInfo });

    // Log error details
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Report to error tracking service
    errorReporting.captureException(error, {
      tags: {
        component: 'ErrorBoundary',
        ...this.props.context,
      },
      extra: {
        componentStack: errorInfo.componentStack,
        ...this.props.context,
      },
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state if props change and resetOnPropsChange is true
    if (
      this.state.hasError &&
      this.props.resetOnPropsChange &&
      prevProps.children !== this.props.children
    ) {
      this.handleReset();
    }
  }

  componentWillUnmount(): void {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  handleReset = (): void => {
    this.props.onReset?.();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleRetry = (): void => {
    // Debounce retry to prevent rapid clicking
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
    
    this.resetTimeoutId = window.setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, context } = this.props;

    if (hasError && error) {
      // Return custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Return default error fallback
      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          onReset={this.handleReset}
          onRetry={this.handleRetry}
          context={context}
        />
      );
    }

    return children;
  }
}

/**
 * Higher-Order Component for adding error boundary to any component
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${Component.displayName || Component.name})`;
  
  return WithErrorBoundary;
}

/**
 * Nested Error Boundary - for use within already error-bounded sections
 * Does not show full UI, just a minimal error message
 */
export class NestedErrorBoundary extends Component<
  Omit<ErrorBoundaryProps, 'fallback'>,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    console.error('Nested error:', error, errorInfo);
    errorReporting.captureException(error, {
      tags: { component: 'NestedErrorBoundary' },
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error!}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          compact
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
